/*
 * Quiz API client — talks to the quiz-api microservice via /quiz proxy.
 */

import { LOGGED_TOKEN_STORAGE_KEY } from '@/common/constants';
import { getRegion } from '@/utils/region';

const QUIZ_BASE = process.env.REACT_APP_QUIZ_API_URL || '/quiz';

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem(LOGGED_TOKEN_STORAGE_KEY);
  let raw = '';
  if (token) {
    try {
      raw = JSON.parse(token);
    } catch {
      raw = token;
    }
  }
  return raw ? { Authorization: raw } : {};
}

async function quizFetch<T = any>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const url = `${QUIZ_BASE}${path}`;
  const res = await fetch(url, {
    ...init,
    headers: { ...authHeaders(), ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `Quiz API error ${res.status}`);
  }
  return res.json();
}

// Public fetch — no auth headers
async function publicFetch<T = any>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const url = `${QUIZ_BASE}${path}`;
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `Quiz API error ${res.status}`);
  }
  return res.json();
}

/** Option keys in an answer: "A,C" -> ["A", "C"]. */
export function answerKeys(answer?: string | null): string[] {
  return (answer || '')
    .toUpperCase()
    .split(/[\s,;]+/)
    .filter(Boolean);
}

/** The answer after clicking `key`: replaces it on a single-answer question,
 *  toggles it on a multiple-response one ("A,C", kept sorted). */
export function pickKey(current: string | undefined, key: string, choose = 1) {
  if (choose <= 1) return key;
  const keys = new Set(answerKeys(current));
  if (keys.has(key.toUpperCase())) keys.delete(key.toUpperCase());
  else keys.add(key.toUpperCase());
  return Array.from(keys).sort().join(',');
}

// ---------- Types ----------

export interface ExamOption {
  key: string;
  text: string;
}

export interface ExamSummary {
  id: number;
  title: string;
  topic: string | null;
  category: string | null;
  is_public: boolean;
  created_at: string;
  created_by: string;
  question_count: number;
}

export interface ExamQuestion {
  id: number;
  position: number;
  stem: string;
  options: ExamOption[];
  /** Options to pick: 1, or 2+ on a multiple-response ("Choose 2") question. */
  choose?: number;
  /** Figure the stem refers to, or null on most questions. */
  image_url?: string | null;
  /** Passage or command shared by a run of questions; shown once above the run. */
  context?: string | null;
}

export interface ExamDetail {
  id: number;
  title: string;
  topic: string | null;
  category: string | null;
  created_at: string;
  created_by: string;
  questions: ExamQuestion[];
}

export interface QuestionResult {
  question_id: number;
  position: number;
  stem: string;
  options: ExamOption[];
  correct_answer: string;
  user_answer: string;
  is_correct: boolean;
  explanation: string;
  /** Walkthrough video for this question, or null on most of them. */
  video_url?: string | null;
  image_url?: string | null;
  context?: string | null;
}

export interface GradeResult {
  score: number;
  total: number;
  percentage: number;
  results: QuestionResult[];
}

export interface SubmitResult extends GradeResult {
  attempt_id: number;
}

export interface AttemptResult extends GradeResult {
  attempt_id: number;
  finished_at: string;
}

export interface CategoryInfo {
  id?: number;
  name: string;
  slug?: string;
  /** Header menu grouping (Canada, US, Brazil, Tech certifications). Server-owned. */
  menu_group?: string | null;
  /** Optional second level under the group, e.g. "Driver License". Server-owned. */
  menu_sub?: string | null;
  topics: string[];
  exam_count?: number;
  question_count?: number;
}

export interface QuizCategory {
  id: number;
  name: string;
  slug: string;
  /** Username that owns a private category; null for a public one. */
  owner?: string | null;
  /** Private categories never appear publicly and cannot be published under. */
  private?: boolean;
}

// ---------- Authenticated API ----------

export function listExams(): Promise<{ exams: ExamSummary[] }> {
  return quizFetch('/api/exams');
}

export function getExam(examId: number): Promise<ExamDetail> {
  return quizFetch(`/api/exams/${examId}`);
}

export function submitExam(
  examId: number,
  answers: Record<string, string>,
): Promise<SubmitResult> {
  return quizFetch(`/api/exams/${examId}/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answers }),
  });
}

export function getResults(examId: number): Promise<AttemptResult> {
  return quizFetch(`/api/exams/${examId}/results`);
}

export function uploadExam(
  file: File,
  title: string,
  topic?: string,
  category?: string,
): Promise<{ exam_id: number; title: string; question_count: number }> {
  const form = new FormData();
  form.append('file', file);
  form.append('title', title);
  if (topic) form.append('topic', topic);
  if (category) form.append('category', category);
  return quizFetch('/api/exams/upload', { method: 'POST', body: form });
}

// Admin-only: queues a document for the compare.sh pipeline (Sonnet writes,
// Gemini reviews, Opus adjudicates) instead of the quick single-pass /upload.
// A script on the dev machine picks it up and processes it there.
export function queueExamUpload(
  file: File,
  title: string,
  category: string,
  topic: string,
  numQuestions: number,
  mode: 'generate' | 'extract',
  keyFile?: File,
  invitees = '',
): Promise<{ job_id: string; status: string }> {
  const form = new FormData();
  form.append('file', file);
  form.append('title', title);
  form.append('category', category);
  form.append('topic', topic);
  form.append('num_questions', String(numQuestions));
  form.append('mode', mode);
  if (keyFile && mode === 'extract') form.append('key_file', keyFile);
  if (invitees.trim()) form.append('invitees', invitees);
  return quizFetch('/api/exams/upload-queue', { method: 'POST', body: form });
}

export function deleteExam(examId: number): Promise<{ detail: string }> {
  return quizFetch(`/api/exams/${examId}`, { method: 'DELETE' });
}

/** Owner or admin only. */
export function renameExam(
  examId: number,
  title: string,
): Promise<{ id: number; title: string }> {
  return quizFetch(`/api/exams/${examId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
  });
}

export function publishExam(
  examId: number,
  isPublic: boolean,
  category?: string,
): Promise<{ id: number; is_public: boolean; category: string | null }> {
  return quizFetch(`/api/exams/${examId}/publish`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ is_public: isPublic, category: category ?? null }),
  });
}

export interface ExamShare {
  id: number;
  identifier: string;
  created_at: string;
}

export interface ExamShareResult extends ExamShare {
  /** True when the identifier was an email with no matching account yet,
   * and a registration invite was created and sent for it in the same
   * action. */
  invited: boolean;
}

export function listExamShares(
  examId: number,
): Promise<{ shares: ExamShare[] }> {
  return quizFetch(`/api/exams/${examId}/shares`);
}

/** Owner or admin: the secret for a private exam's invite link; reset makes a new one. */
export function getShareLink(
  examId: number,
  reset = false,
): Promise<{ token: string }> {
  return quizFetch(`/api/exams/${examId}/share-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reset }),
  });
}

/** Opening an invite link: adds the signed-in user to the exam's shares. */
export function joinExam(
  examId: number,
  token: string,
): Promise<{ id: number }> {
  return quizFetch(`/api/exams/${examId}/join`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
}

export function shareExam(
  examId: number,
  identifier: string,
): Promise<ExamShareResult> {
  return quizFetch(`/api/exams/${examId}/shares`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier }),
  });
}

export function unshareExam(
  examId: number,
  shareId: number,
): Promise<{ detail: string }> {
  return quizFetch(`/api/exams/${examId}/shares/${shareId}`, {
    method: 'DELETE',
  });
}

export interface UploadJob {
  job_id: string;
  title: string;
  state: 'queued' | 'processing' | 'done' | 'failed';
  stage?: string;
  percent: number;
  exam_ids?: number[];
  message?: string;
}

/** The caller's own imports through the pipeline, for the header bar. */
export function getUploadStatus(): Promise<{ jobs: UploadJob[] }> {
  return quizFetch('/api/exams/upload-queue/status');
}

export function listCategories(): Promise<{ categories: QuizCategory[] }> {
  return quizFetch('/api/categories');
}

export function createCategory(name: string): Promise<QuizCategory> {
  return quizFetch('/api/categories', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
}

export function deleteCategory(id: number): Promise<{ detail: string }> {
  return quizFetch(`/api/categories/${id}`, { method: 'DELETE' });
}

// ---------- Public API (no auth) ----------

export function listPublicCategories(): Promise<{
  categories: CategoryInfo[];
}> {
  return publicFetch(`/api/public/categories?region=${getRegion()}`);
}

export function listPublicExams(
  category?: string,
  topic?: string,
  group?: string,
): Promise<{ exams: ExamSummary[] }> {
  const params = new URLSearchParams();
  if (category) params.set('category', category);
  if (topic) params.set('topic', topic);
  if (group) params.set('group', group);
  params.set('region', getRegion());
  const qs = params.toString();
  return publicFetch(`/api/public/exams${qs ? `?${qs}` : ''}`);
}

export function getPublicExam(
  examId: number | string,
  query?: string,
): Promise<ExamDetail> {
  return publicFetch(`/api/public/exams/${examId}${query ? `?${query}` : ''}`);
}

export function submitPublicExam(
  examId: number | string,
  answers: Record<string, string>,
  // The question ids this sitting showed, when the student took fewer than
  // all of them; the server then grades exactly those.
  served?: number[],
): Promise<GradeResult> {
  // quizFetch, not publicFetch: a signed-in student's sitting is saved to
  // their history; a guest's is only graded.
  return quizFetch(`/api/public/exams/${examId}/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(served ? { answers, served } : { answers }),
  });
}

// Mixed test — the server describes the available options (categories, tests,
// default/min/max question count, modes) so clients never hardcode them.
export interface MixedExamOption {
  id: number;
  title: string;
  question_count: number;
}

export interface MixedCategoryOption {
  name: string;
  exams: MixedExamOption[];
  total_questions: number;
}

export interface MixedOptions {
  defaults: { count: number; min_count: number; max_count: number };
  modes: { id: string; label: string }[];
  categories: MixedCategoryOption[];
}

export function getMixedOptions(): Promise<MixedOptions> {
  return publicFetch(`/api/public/mixed/options?region=${getRegion()}`);
}

// Practice mode: grade a single answer. The answer key is never sent to the
// client up front — it's requested per question, after the user commits.
export interface AnswerCheck {
  question_id: number;
  correct: boolean;
  correct_answer: string;
  explanation: string;
  /** Walkthrough video for this question, or null on most of them. */
  video_url?: string | null;
}

export function checkAnswer(
  questionId: number,
  answer: string,
): Promise<AnswerCheck> {
  return publicFetch(`/api/public/questions/${questionId}/check`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answer }),
  });
}

// ---------- Notes ----------
// A signed-in user's private written note on a question — one per question,
// read only by its author and admins.

export const MAX_NOTE_CHARS = 2000;

export interface QuestionNote {
  question_id: number;
  text: string;
  updated_at: string | null;
}

export function getMyNotes(
  questionIds: number[],
): Promise<{ notes: Record<string, QuestionNote> }> {
  return quizFetch(`/api/notes?question_ids=${questionIds.join(',')}`);
}

/** Saves the note; empty text deletes it. */
export function saveNote(
  questionId: number,
  text: string,
): Promise<QuestionNote> {
  return quizFetch(`/api/notes/${questionId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
}

export interface AdminNote extends QuestionNote {
  id: number;
  username: string;
  exam_id: number;
  exam_title: string;
  stem: string;
}

export function adminListNotes(params: {
  q?: string;
  page?: number;
  page_size?: number;
}): Promise<{ count: number; notes: AdminNote[] }> {
  const qs = new URLSearchParams(
    Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== '')
      .map(([k, v]) => [k, String(v)]),
  );
  return quizFetch(`/api/admin/notes?${qs}`);
}

export function adminDeleteNote(noteId: number): Promise<unknown> {
  return quizFetch(`/api/admin/notes/${noteId}`, { method: 'DELETE' });
}
